import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '../use.store';
import SearchPoiCard from '../components/SearchPoiCard';
import { getShapeByPoi } from '../utils/functions';
import Lottie from 'react-lottie';
import notFound from '../img/animation/Scape - NO RESULT.json';
import SearchSuggestionPois from '../components/SearchSuggestionPois';

const Search = observer(({ interactiveRef }) => {
    const { mapStore, mainStore } = useStores();

    useEffect(() => {
        mainStore.updateFilteredPois();
    }, [mainStore.searchKeyword]);

    const historiesStorage = localStorage.getItem('historiesSearchPoi')
        ? JSON.parse(localStorage.getItem('historiesSearchPoi'))
        : [];

    const onClickPoi = (poi) => {
        if (!historiesStorage.some((item) => item._id === poi._id)) {
            localStorage.setItem(
                'historiesSearchPoi',
                JSON.stringify([...historiesStorage, poi])
            );
        }

        const shape = getShapeByPoi(poi, mapStore.freeShapes);

        if (mainStore.typeSearchHistory === 'start') {
            mainStore.setStartPoi(poi);

            if (shape) {
                mainStore.setWayfindingShapeFrom(shape);
                mapStore.setStartShape(shape);
            }
            mainStore.resetSearchHistory();
        } else {
            if (
                mainStore.destinationPoi?._id === poi._id &&
                mainStore.startPoi?._id !== poi._id
            ) {
                mainStore.setIsPoiOverlay(true);
                mainStore.setSelectedPoiDetail(poi);
            }
            mainStore.setDestinationPoi(poi);
            if (shape) {
                mainStore.setWayfindingShapeTo(shape);
                mapStore.setDestinationShape(shape);
            }
            mainStore.resetSearchHistory();
        }

        interactiveRef.current?.changeActiveFloor(shape?.floorId);
    };

    return (
        <div
            className={`absolute inset-0 h-[100dvh] w-screen px-4 pb-4 space-y-4 md:space-y-6 bg-[#F2F2F2] z-[70]
          flex flex-col transition-all duration-[450ms] ease-out
          ${
              mainStore.typeSearchHistory
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-full opacity-0 pointer-events-none'
          }
        `}
        >
            <div className="flex items-center gap-4 mt-10">
                <div className="flex flex-1 items-center gap-3 bg-white border border-[#1C1C1C] px-3 py-[10px]">
                    <input
                        value={mainStore.searchKeyword ?? ''}
                        onChange={(e) =>
                            mainStore.setSearchKeyword(e.target.value)
                        }
                        placeholder
                        className="flex-1 text-[14px] md:text-[16px] leading-[22px] sm:leading-[28px] outline-none text-black placeholder:text-[#9C9C9C]"
                    ></input>
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                            stroke="#C2C2C2"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M21.0004 20.9984L16.6504 16.6484"
                            stroke="#C2C2C2"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
                <svg
                    className="cursor-pointer"
                    onClick={() => mainStore.resetSearchHistory()}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <g clip-path="url(#clip0_4050_12909)">
                        <path
                            d="M20.4853 3.51473C18.2188 1.24819 15.2052 0 12 0C8.79469 0 5.78123 1.24819 3.51473 3.51469C1.24823 5.78119 0 8.79469 0 12C0 15.2054 1.24819 18.2188 3.51473 20.4853C5.78119 22.7518 8.79469 24 12 24C15.2052 24 18.2188 22.7518 20.4853 20.4853C22.7518 18.2188 24 15.2053 24 12C24 8.79464 22.7518 5.78119 20.4853 3.51473ZM17.29 16.2957C17.5645 16.5703 17.5645 17.0155 17.29 17.2901C17.1526 17.4274 16.9727 17.496 16.7928 17.496C16.6129 17.496 16.4329 17.4273 16.2957 17.2901L12 12.9944L7.70433 17.2901C7.56698 17.4274 7.38708 17.496 7.20712 17.496C7.02722 17.496 6.84722 17.4273 6.71002 17.2901C6.43542 17.0155 6.43542 16.5703 6.71002 16.2957L11.0056 12L6.71002 7.70438C6.43542 7.42983 6.43542 6.98461 6.71002 6.71002C6.98456 6.43547 7.42973 6.43547 7.70438 6.71002L12 11.0057L16.2956 6.71002C16.5702 6.43547 17.0154 6.43547 17.29 6.71002C17.5645 6.98456 17.5645 7.42978 17.29 7.70438L12.9943 12L17.29 16.2957Z"
                            fill="#C7C7C7"
                        />
                    </g>
                    <defs>
                        <clipPath id="clip0_4050_12909">
                            <rect width="24" height="24" fill="white" />
                        </clipPath>
                    </defs>
                </svg>
            </div>
            <p className="text-[#556675] font-bold text-[12px] md:text-base leading-[18px]">
                {mainStore.searchKeyword ? 'Results' : 'Search History'}
            </p>

            <div
                className={`${
                    !mainStore.searchKeyword &&
                    historiesStorage?.length === 0 &&
                    'flex justify-center'
                } flex-1 space-y-3 scrollbar scrollable overflow-y-auto pb-4`}
            >
                {mainStore.searchKeyword ? (
                    mainStore.filteredPois?.length > 0 ? (
                        mainStore.filteredPois?.map((poi) => (
                            <SearchPoiCard
                                poi={poi}
                                onClick={() => onClickPoi(poi)}
                            />
                        ))
                    ) : (
                        <div className="flex flex-1 flex-col justify-center items-center mt-20">
                            <div className="w-[120px] h-[120px] mb-6">
                                <Lottie
                                    options={{
                                        loop: true,
                                        autoplay: true,
                                        animationData: notFound,
                                        rendererSettings: {
                                            preserveAspectRatio:
                                                'xMidYMid slice',
                                        },
                                    }}
                                    style={{ flex: 1 }}
                                />
                            </div>
                            <div className="space-y-[6px] text-center">
                                <p className="text-[12px] md:text-base text-[##44444] font-semibold">
                                    No Result Found
                                </p>
                                <p className="text-[8px] md:text-[12px] text-[#858585] font-regular">
                                    Sorry, try adjusting your search to find
                                    <br />
                                    what you are looking for.
                                </p>
                            </div>
                        </div>
                    )
                ) : historiesStorage?.length > 0 ? (
                    historiesStorage.map((poi) => (
                        <div className="space-y-4">
                            <div
                                className="flex items-center gap-3 cursor-pointer"
                                onClick={() => onClickPoi(poi)}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M10.0003 18.9557C5.05866 18.9557 1.04199 14.9391 1.04199 9.9974C1.04199 5.05573 5.05866 1.03906 10.0003 1.03906C14.942 1.03906 18.9587 5.05573 18.9587 9.9974C18.9587 14.9391 14.942 18.9557 10.0003 18.9557ZM10.0003 2.28906C5.75033 2.28906 2.29199 5.7474 2.29199 9.9974C2.29199 14.2474 5.75033 17.7057 10.0003 17.7057C14.2503 17.7057 17.7087 14.2474 17.7087 9.9974C17.7087 5.7474 14.2503 2.28906 10.0003 2.28906Z"
                                        fill="#4999A3"
                                    />
                                    <path
                                        d="M13.0919 13.2745C12.9835 13.2745 12.8752 13.2495 12.7752 13.1828L10.1919 11.6411C9.55019 11.2578 9.0752 10.4161 9.0752 9.67448V6.25781C9.0752 5.91615 9.35853 5.63281 9.7002 5.63281C10.0419 5.63281 10.3252 5.91615 10.3252 6.25781V9.67448C10.3252 9.97448 10.5752 10.4161 10.8335 10.5661L13.4169 12.1078C13.7169 12.2828 13.8085 12.6661 13.6335 12.9661C13.5085 13.1661 13.3002 13.2745 13.0919 13.2745Z"
                                        fill="#4999A3"
                                    />
                                </svg>
                                <p className="text-[12px] md:text-base leading-[22px] text-[#3D3D3D] font-semibold">
                                    {poi.name}
                                </p>
                            </div>
                            <div className="bg-[#DFDFDF] w-full h-[1px]" />
                        </div>
                    ))
                ) : (
                    <div className="text-base xl:text-[24px] font-regular text-center mt-20">
                        No Search History
                    </div>
                )}
            </div>

            {!(
                mainStore.searchKeyword && mainStore.filteredPois?.length > 0
            ) && (
                <div>
                    <p className="text-[#556675] font-bold text-[12px] md:text-base leading-[18px] mb-4 md:mb-6">
                        Suggestion
                    </p>
                    <SearchSuggestionPois onClickPoi={onClickPoi} />
                </div>
            )}
        </div>
    );
});

export default Search;
